import { PaymentStatus } from '@repo/common';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, PropsWithChildren, useState } from 'react';

import { Option } from '@components/ui/autocomplete/auto-complete-input';
import { ResetFilterButton } from '@components/ui/button';
import {
    AutoCompleteFilter,
    SelectFilter,
    TextSearchFilter,
} from '@components/ui/search-filter';

import { UrlSearchFilterProperty } from '@/constants';
import { useGetSuppliersQuery } from '@/features/slice/suppliers';
import { useGetUsersQuery } from '@/features/slice/users';
import { useUrlFilter } from '@/hooks';

export const PurchaseTableFilters: FC<PropsWithChildren> = ({ children }) => {
    const { setSearchText, setSearchFilter, getSearchFilter, searchText } =
        useUrlFilter();

    const [supplierSearchText, setSupplierSearchText] = useState('');
    const [userSearchText, setUserSearchText] = useState('');

    const userIdFilter = getSearchFilter(UrlSearchFilterProperty.USER_ID) || '';
    const userNameFilter =
        getSearchFilter(UrlSearchFilterProperty.USER_NAME) || '';
    const supplierIdFilter =
        getSearchFilter(UrlSearchFilterProperty.SUPPLIER_ID) || '';
    const supplierNameFilter =
        getSearchFilter(UrlSearchFilterProperty.SUPPLIER_NAME) || '';
    const paymentStatusFilter =
        getSearchFilter(UrlSearchFilterProperty.PAYMENT_STATUS) || '';

    const debouncedUserSearchText = useDebounce(userSearchText, 500);
    const debouncedSupplierSearchText = useDebounce(supplierSearchText, 500);

    const {
        data: usersData,
        isLoading: isUserLoading,
        isFetching: isUserFetching,
    } = useGetUsersQuery({
        search: debouncedUserSearchText,
    });

    const {
        data: suppliersData,
        isLoading: isSupplierLoading,
        isFetching: isSupplierFetching,
    } = useGetSuppliersQuery({
        search: debouncedSupplierSearchText,
    });

    const users = usersData?.data?.map((user) => ({
        label: user.name as string,
        value: user.id,
    }));

    const suppliers = suppliersData?.data?.map((supplier) => ({
        label: supplier.name,
        value: supplier.id,
    }));

    const handleSelectUser = (selectedValue: Option) => {
        setSearchFilter(UrlSearchFilterProperty.USER_ID, selectedValue.value);
        setSearchFilter(UrlSearchFilterProperty.USER_NAME, selectedValue.label);
    };

    const handleSelectSupplier = (selectedValue: Option) => {
        setSearchFilter(
            UrlSearchFilterProperty.SUPPLIER_ID,
            selectedValue.value,
        );
        setSearchFilter(
            UrlSearchFilterProperty.SUPPLIER_NAME,
            selectedValue.label,
        );
    };

    const userValue = {
        label: supplierNameFilter,
        value: supplierIdFilter,
    };

    const supplierValue = {
        label: userNameFilter,
        value: userIdFilter,
    };

    return (
        <div className="flex flex-col flex-wrap items-center justify-start gap-4 sm:flex-row sm:items-center">
            <TextSearchFilter
                onChangeSearch={(e) => setSearchText(e.target.value)}
                placeholder="purchase number"
                value={searchText}
            />

            <SelectFilter
                // title="Status"
                placeholder="Payment Status"
                onChange={(value) =>
                    setSearchFilter(
                        UrlSearchFilterProperty.PAYMENT_STATUS,
                        value,
                    )
                }
                value={paymentStatusFilter}
                options={[
                    { label: 'Paid', value: PaymentStatus.PAID },
                    { label: 'Due', value: PaymentStatus.DUE },
                ]}
            />

            <AutoCompleteFilter
                options={suppliers ?? []}
                value={supplierValue}
                onValueChange={handleSelectSupplier}
                placeholder="Select Supplier"
                isLoading={isSupplierLoading || isSupplierFetching}
                onInputChange={(value) => setSupplierSearchText(value)}
            />

            <AutoCompleteFilter
                options={users ?? []}
                value={userValue}
                onValueChange={handleSelectUser}
                placeholder="Select User"
                isLoading={isUserLoading || isUserFetching}
                onInputChange={(value) => setUserSearchText(value)}
            />

            {children}

            <ResetFilterButton />
        </div>
    );
};

PurchaseTableFilters.displayName = 'PurchaseTableFilters';
